import _ from "lodash";
import { BRANDED_TERM_TYPES, PACK_TERM_TYPES } from "../constant/drugs";
import moment from "moment-timezone";

export const formatRxnormDrugLabelForSearch = (pinName, bnName) => {
  return bnName ? `${pinName} (${bnName})` : pinName;
};

export const isRxNormDrugBranded = ({ tty }) => Object.values(BRANDED_TERM_TYPES).includes(tty);

export const isPackRxnormDrug = ({ tty }) => Object.values(PACK_TERM_TYPES).includes(tty);

export const formatPapRxnormDrugName = (rxnormDrug) => {
  if (!rxnormDrug) return "";
  if (isPackRxnormDrug(rxnormDrug)) {
    return rxnormDrug.bpckName || rxnormDrug.gpckName || rxnormDrug.tmDisplayName || "";
  }
  return rxnormDrug.bnName || rxnormDrug.pinName?.split("|").join(" + ") || "";
};

// Mirrors backend `Pap.getRxnormDrugDisplayName`. Pack-blind by design — used for
// what we persist to `associatedDrugs`, where FE/BE parity matters more than
// pack-variant disambiguation.
export const formatRxnormDrugNamePersistable = (rxnormDrug) => {
  if (!rxnormDrug) return "";
  return rxnormDrug.bnName || rxnormDrug.pinName?.split("|").join(" + ") || "";
};

// Friendly per-drug name for read-only display surfaces. Prefers `tmDisplayName`
// for packs (e.g. "Taperdex (dexamethasone) Pack") so a list of packs in the
// same drug family collapses to one entry on dedupe.
export const formatPapRxnormDrugNameFriendly = (rxnormDrug) => {
  if (!rxnormDrug) return "";
  if (isPackRxnormDrug(rxnormDrug)) {
    return rxnormDrug.tmDisplayName || rxnormDrug.bpckName || rxnormDrug.gpckName || "";
  }
  return rxnormDrug.bnName || rxnormDrug.pinName?.split("|").join(" + ") || "";
};

/**
 * Canonical alphabetical sort for a list of rxnormDrug objects intended for display.
 * Keys off `formatPapRxnormDrugName` so the sort order matches the rendered label
 * (brand preferred for non-packs; bpckName/gpckName for packs). Returns a new array.
 */
export const sortDrugsByDisplayName = (drugs) =>
  [...drugs].sort((a, b) => formatPapRxnormDrugName(a).localeCompare(formatPapRxnormDrugName(b)));

export const algorithmOneDrugsSorting = (drugs) =>
  drugs.sort((a, b) => {
    if (a.label === b.label) {
      return (a.bnName || "").localeCompare(b.bnName || "", { numeric: true });
    } else {
      return a.label.localeCompare(b.label, { numeric: true });
    }
  });

export const getAPDrugDisplayName = (rxnormDrug) => {
  const { bnName, pinName, dfName, bpckName, gpckName, tty } = rxnormDrug;

  // For packs, return the full pack name without appending strength/dose form
  if (isPackRxnormDrug({ tty })) {
    return bpckName || gpckName;
  }

  let displayName = pinName.split("|").join(" + ");

  if (bnName) {
    displayName = `${bnName} (${displayName})`;
  }

  if (dfName) {
    displayName = `${displayName} ${dfName}`;
  }

  return displayName;
};

export const getActiveDrugs = (patientMedications) => {
  if (!patientMedications) return [];

  const activeDrugEvents = patientMedications?.map(({ drugEntries }) => {
    if (!drugEntries?.length) return [];

    const relevantDrugs =
      drugEntries?.filter((drugEntry) => {
        const relevantDates = [];
        const { numberOfCycles, daysBetweenCycles, cycle, numberOfRefillsAuthorized, date } = drugEntry;
        const cyclesAmount = numberOfCycles || numberOfRefillsAuthorized || 1;

        if (cyclesAmount === 1) {
          return moment(date).isBetween(moment().subtract(6, "month"), moment().add(6, "month"));
        }

        relevantDates.push(moment(date));
        for (let i = cycle + 1; i <= cyclesAmount; i++) {
          relevantDates.push(moment(date).add(daysBetweenCycles * i, "d"));
        }

        const isDateWithinHalfYearRange = relevantDates.find((date) =>
          moment(date).isBetween(moment().subtract(6, "month"), moment().add(6, "month"))
        );
        return isDateWithinHalfYearRange;
      }) || [];
    return relevantDrugs;
  });

  const activeDrugsEventsFiltered = _.flatten(activeDrugEvents);
  const activeDrugs = activeDrugsEventsFiltered.map(({ rxnormDrug }) => {
    const { bnName, pinName } = rxnormDrug;
    const pinDisplayName = pinName.split("|").join(" + ");

    return {
      ...rxnormDrug,
      label: bnName ? `${bnName} (${pinDisplayName})` : pinDisplayName
    };
  });

  const uniqActiveDrugs = _.uniqBy(activeDrugs, "id");

  return uniqActiveDrugs;
};
